import {
  faExclamationCircle,
  faPaste,
  faSave,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { cloneDeep } from "lodash-es";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getDefaultPageId, getPage } from "@/hooks/useQueries/pages";

import useQueries from "../../hooks/useQueries";
import styles from "./PublicLink.module.scss";

export default function PublicLink() {
  const { currentProject, promptTemplates, updatePage } = useQueries();

  const { data: defaultPageId } = useQuery({
    queryKey: ["default-page"],
    queryFn: ({ signal }) => getDefaultPageId(signal, currentProject?.id),
    enabled: Boolean(currentProject?.id),
    retry: false,
  });

  const { data: page } = useQuery({
    queryKey: ["pages", currentProject?.id, defaultPageId],
    queryFn: ({ signal }) => getPage(signal, currentProject?.id, defaultPageId),
    enabled: Boolean(currentProject?.id && defaultPageId),
    retry: false,
  });

  const [showEditor, setShowEditor] = useState<boolean>(true);
  const [showGraph, setShowGraph] = useState<boolean>(true);
  const [showLLM, setShowLLM] = useState<boolean>(true);
  const [buddy, setBuddy] = useState<string>("87e503e8-14ba-4e95-b31c-e626d5cb7752");
  const [showPrompts, setShowPrompts] = useState<boolean>(false);
  const [defaultPrompt1, setDefaultPrompt1] = useState<string>("");
  const [defaultPrompt2, setDefaultPrompt2] = useState<string>("");
  const [defaultPrompt3, setDefaultPrompt3] = useState<string>("");
  const [defaultPrompt4, setDefaultPrompt4] = useState<string>("");

  // Button states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isLinkCopied, setIsLinkCopied] = useState<boolean>(false);

  useEffect(() => {
    if (page) {
      const config = page.meta.publicConfig;
      if (!config) return;
      if ("showEditor" in config) setShowEditor(config.showEditor);
      if ("showGraph" in config) setShowGraph(config.showGraph);
      if ("showLLM" in config) setShowLLM(config.showLLM);
      if ("buddy" in config) setBuddy(config.buddy);
      if ("showPrompts" in config) setShowPrompts(config.showPrompts);
      if ("defaultPrompt1" in config) setDefaultPrompt1(config.defaultPrompt1);
      if ("defaultPrompt2" in config) setDefaultPrompt2(config.defaultPrompt2);
      if ("defaultPrompt3" in config) setDefaultPrompt3(config.defaultPrompt3);
      if ("defaultPrompt4" in config) setDefaultPrompt4(config.defaultPrompt4);
    }
  }, [page]);

  const updateMeta = async () => {
    if (!page) return;

    if (!isDirty) {
      const link = location.protocol + "//" + location.host + "/public/" + currentProject?.publicID;
      navigator.clipboard.writeText(link);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
      return;
    }

    setIsLoading(true);
    const newMeta = cloneDeep(page?.meta);

    if (!("publicConfig" in newMeta)) {
      newMeta["publicConfig"] = {};
    }
    newMeta["publicConfig"] = {
      ...newMeta["publicConfig"],
      showEditor,
      showGraph,
      showLLM,
      buddy,
      showPrompts,
      defaultPrompt1,
      defaultPrompt2,
      defaultPrompt3,
      defaultPrompt4,
    };
    try {
      await updatePage({
        page,
        content: { meta: JSON.stringify(newMeta) },
      });
      setIsLoading(false);
      setIsSubmitted(true);
      setIsDirty(false);
      setTimeout(() => setIsSubmitted(false), 2000);
    } catch (e) {
      setIsLoading(false);
      setIsError(true);
      setTimeout(() => setIsError(false), 2000);
      toast.error("Error saving, please try again");
    }
  };

  const restoreDefaults = () => {
    setShowEditor(true);
    setShowGraph(true);
    setShowLLM(true);
    setBuddy("6e178181-2f64-4f8d-baa3-d9b79637457e");
    setShowPrompts(false);
    setDefaultPrompt1("");
    setDefaultPrompt2("");
    setDefaultPrompt3("");
    setDefaultPrompt4("");
  };

  return (
    <div className={styles.publicLink}>
      <h3>Views</h3>
      <div className={styles.row}>
        <label>Show Editor</label>
        <div
          className={classNames(styles.checkbox, { [styles.checked]: showEditor })}
          onClick={() => {
            setShowEditor(!showEditor);
            setIsDirty(true);
          }}
        >
          <FontAwesomeIcon icon={faCheck} size="xs" />
        </div>
      </div>
      <div className={styles.row}>
        <label>Show Graph</label>
        <div
          className={classNames(styles.checkbox, { [styles.checked]: showGraph })}
          onClick={() => {
            setShowGraph(!showGraph);
            setIsDirty(true);
          }}
        >
          <FontAwesomeIcon icon={faCheck} size="xs" />
        </div>
      </div>
      <div className={styles.row}>
        <label>Show LLM</label>
        <div
          className={classNames(styles.checkbox, { [styles.checked]: showLLM })}
          onClick={() => {
            setShowLLM(!showLLM);
            setIsDirty(true);
          }}
        >
          <FontAwesomeIcon icon={faCheck} size="xs" />
        </div>
      </div>
      <div className={styles.divider} style={{ marginTop: 0 }}></div>
      <h3>Buddies</h3>
      <div className={styles.row}>
        <label>Default Buddy</label>
        <div className={styles.dropdown}>
          <select
            onChange={(e) => {
              setBuddy(e.target.value);
              setIsDirty(true);
            }}
            value={buddy}
          >
            {promptTemplates.map((buddy) => (
              <option value={buddy.id}>{buddy.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className={styles.row}>
        <label>Default Prompts</label>
        <div
          className={styles.prompts}
          onClick={() => {
            setShowPrompts(!showPrompts);
            setIsDirty(true);
          }}
        >
          {showPrompts ? "Hide" : "Show"} prompts
        </div>
      </div>
      {showPrompts && (
        <div className={styles.promptInputs}>
          <div className={styles.row}>
            <label>1.</label>
            <input
              type="text"
              onChange={(e) => {
                setDefaultPrompt1(e.target.value);
                setIsDirty(true);
              }}
              value={defaultPrompt1}
            />
          </div>
          <div className={styles.row}>
            <label>2.</label>
            <input
              type="text"
              onChange={(e) => {
                setDefaultPrompt2(e.target.value);
                setIsDirty(true);
              }}
              value={defaultPrompt2}
            />
          </div>
          <div className={styles.row}>
            <label>3.</label>
            <input
              type="text"
              onChange={(e) => {
                setDefaultPrompt3(e.target.value);
                setIsDirty(true);
              }}
              value={defaultPrompt3}
            />
          </div>
          <div className={styles.row}>
            <label>4.</label>
            <input
              type="text"
              onChange={(e) => {
                setDefaultPrompt4(e.target.value);
                setIsDirty(true);
              }}
              value={defaultPrompt4}
            />
          </div>
        </div>
      )}
      <div className={styles.divider} style={{ marginTop: 0 }}></div>
      <button
        onClick={updateMeta}
        className={classNames({
          [styles.isLoading]: isLoading,
          [styles.isSubmitted]: isSubmitted,
          [styles.isError]: isError,
          [styles.isDirty]: isDirty,
          [styles.isLinkCopied]: isLinkCopied,
        })}
      >
        {isError ? (
          <>
            <FontAwesomeIcon icon={faExclamationCircle} /> Error saving!
          </>
        ) : isLoading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin /> Loading
          </>
        ) : isSubmitted ? (
          <>
            <FontAwesomeIcon icon={faCheck} /> Saved!
          </>
        ) : isLinkCopied ? (
          <>
            <FontAwesomeIcon icon={faCheck} /> Link copied!
          </>
        ) : isDirty ? (
          <>
            <FontAwesomeIcon icon={faSave} /> Save configuration
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faPaste} /> Copy read only link
          </>
        )}
      </button>
      <div className={styles.divider} style={{ marginTop: 0 }}></div>
      <div className={classNames(styles.row, styles.footer)}>
        <a href="#" onClick={restoreDefaults}>
          Restore Defaults
        </a>
        {/* <a href="#" onClick={deleteLink}>
          Delete Link
        </a> */}
      </div>
    </div>
  );
}
